const FLATFINDER_UPDATE_CHANNEL = 'realitiq-flatfinder-updates';
const FLATFINDER_UPDATE_STORAGE_KEY = 'realitiq:flatfinder:update';

type FlatfinderPublicUpdate = {
  publicId: string;
  revision: number;
  updatedAt: number;
};

function isFlatfinderPublicUpdate(value: unknown): value is FlatfinderPublicUpdate {
  if (!value || typeof value !== 'object') return false;
  const update = value as Partial<FlatfinderPublicUpdate>;
  return (
    typeof update.publicId === 'string' &&
    typeof update.revision === 'number' &&
    typeof update.updatedAt === 'number'
  );
}

export function announceFlatfinderPublicUpdate(publicId: string | null, revision: number) {
  if (!publicId) return;
  const update: FlatfinderPublicUpdate = {
    publicId,
    revision,
    updatedAt: Date.now(),
  };

  try {
    const channel = new BroadcastChannel(FLATFINDER_UPDATE_CHANNEL);
    channel.postMessage(update);
    channel.close();
  } catch {
    // Storage events remain available when BroadcastChannel is unavailable.
  }

  try {
    window.localStorage.setItem(FLATFINDER_UPDATE_STORAGE_KEY, JSON.stringify(update));
  } catch {
    // Periodic public revalidation remains the final fallback.
  }
}

export function subscribeToFlatfinderPublicUpdates(
  onUpdate: (update: FlatfinderPublicUpdate) => void
) {
  let lastUpdate = '';
  const deliver = (update: FlatfinderPublicUpdate) => {
    const updateKey = `${update.publicId}:${update.revision}:${update.updatedAt}`;
    if (lastUpdate === updateKey) return;
    lastUpdate = updateKey;
    onUpdate(update);
  };
  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(FLATFINDER_UPDATE_CHANNEL);
    channel.addEventListener('message', ({ data }) => {
      if (isFlatfinderPublicUpdate(data)) deliver(data);
    });
  } catch {
    channel = null;
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== FLATFINDER_UPDATE_STORAGE_KEY || !event.newValue) return;
    try {
      const update: unknown = JSON.parse(event.newValue);
      if (isFlatfinderPublicUpdate(update)) deliver(update);
    } catch {
      // Ignore invalid values written by older clients.
    }
  };
  window.addEventListener('storage', handleStorage);

  return () => {
    channel?.close();
    window.removeEventListener('storage', handleStorage);
  };
}
